import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTenantPackagePlan,
  confirmTenantSessionCharge,
  getTenantAthleteBilling,
  getTenantBillingOverview,
  recordManualBookingPayment,
  recordTenantPackageSale,
  requestTenantSessionCharge,
  markTenantSessionChargeNotReceived,
  useBookingPackageCredit as applyBookingPackageCredit,
} from '../lib/billing';
import type {
  TenantAthleteBillingResponse,
  TenantBillingOverviewResponse,
  TenantManualBookingPaymentPayload,
  TenantSessionCharge,
  TenantSessionChargeNotReceivedPayload,
  TenantSessionChargeRequestPayload,
  TenantPackagePlan,
  TenantPackagePlanPayload,
  TenantPackageSalePayload,
  TenantUsePackageCreditPayload,
} from '../types';
import { scheduleKeys } from './useScheduleDashboard';

export const tenantBillingKeys = {
  all: ['tenant-billing'] as const,
  overview: () => [...tenantBillingKeys.all, 'overview'] as const,
  athlete: (athleteId?: number | null) => [...tenantBillingKeys.all, 'athlete', athleteId] as const,
};

export function useTenantBillingOverview(enabled: boolean = true) {
  return useQuery<TenantBillingOverviewResponse>({
    queryKey: tenantBillingKeys.overview(),
    queryFn: getTenantBillingOverview,
    staleTime: 60 * 1000,
    retry: false,
    enabled,
  });
}

export function useTenantAthleteBilling(athleteId?: number | null, enabled: boolean = true) {
  return useQuery<TenantAthleteBillingResponse>({
    queryKey: tenantBillingKeys.athlete(athleteId),
    queryFn: () => getTenantAthleteBilling(athleteId as number),
    staleTime: 30 * 1000,
    retry: false,
    enabled: enabled && athleteId != null,
  });
}

export function useCreateTenantPackagePlan() {
  const queryClient = useQueryClient();
  return useMutation<TenantPackagePlan, Error, TenantPackagePlanPayload>({
    mutationFn: createTenantPackagePlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantBillingKeys.all });
    },
  });
}

export function useRecordTenantPackageSale() {
  const queryClient = useQueryClient();
  return useMutation<
    TenantAthleteBillingResponse,
    Error,
    { athleteId: number; payload: TenantPackageSalePayload }
  >({
    mutationFn: ({ athleteId, payload }) => recordTenantPackageSale(athleteId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: tenantBillingKeys.overview() });
      queryClient.invalidateQueries({ queryKey: tenantBillingKeys.athlete(variables.athleteId) });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
    },
  });
}

export function useRecordManualBookingPayment() {
  const queryClient = useQueryClient();
  return useMutation<
    TenantAthleteBillingResponse,
    Error,
    { bookingId: number; occurrenceId?: number | null; athleteId?: number | null; payload: TenantManualBookingPaymentPayload }
  >({
    mutationFn: ({ bookingId, payload }) => recordManualBookingPayment(bookingId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: tenantBillingKeys.overview() });
      queryClient.invalidateQueries({ queryKey: tenantBillingKeys.athlete(variables.athleteId) });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(variables.occurrenceId) });
    },
  });
}

export function useUseBookingPackageCredit() {
  const queryClient = useQueryClient();
  return useMutation<
    TenantAthleteBillingResponse,
    Error,
    { bookingId: number; occurrenceId?: number | null; athleteId?: number | null; payload?: TenantUsePackageCreditPayload }
  >({
    mutationFn: ({ bookingId, payload }) => applyBookingPackageCredit(bookingId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: tenantBillingKeys.overview() });
      queryClient.invalidateQueries({ queryKey: tenantBillingKeys.athlete(variables.athleteId) });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(variables.occurrenceId) });
    },
  });
}

function invalidateSessionPaymentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  occurrenceId?: number | null,
  athleteId?: number | null
) {
  queryClient.invalidateQueries({ queryKey: tenantBillingKeys.overview() });
  queryClient.invalidateQueries({ queryKey: tenantBillingKeys.athlete(athleteId) });
  queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
  queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(occurrenceId) });
}

export function useRequestTenantSessionCharge() {
  const queryClient = useQueryClient();
  return useMutation<
    TenantSessionCharge,
    Error,
    { bookingId: number; occurrenceId?: number | null; athleteId?: number | null; payload: TenantSessionChargeRequestPayload }
  >({
    mutationFn: ({ bookingId, payload }) => requestTenantSessionCharge(bookingId, payload),
    onSuccess: (_data, variables) => invalidateSessionPaymentQueries(queryClient, variables.occurrenceId, variables.athleteId),
  });
}

export function useConfirmTenantSessionCharge() {
  const queryClient = useQueryClient();
  return useMutation<
    TenantSessionCharge,
    Error,
    { bookingId: number; chargeId: number; occurrenceId?: number | null; athleteId?: number | null }
  >({
    mutationFn: ({ bookingId, chargeId }) => confirmTenantSessionCharge(bookingId, chargeId),
    onSuccess: (_data, variables) => invalidateSessionPaymentQueries(queryClient, variables.occurrenceId, variables.athleteId),
  });
}

export function useMarkTenantSessionChargeNotReceived() {
  const queryClient = useQueryClient();
  return useMutation<
    TenantSessionCharge,
    Error,
    { bookingId: number; chargeId: number; occurrenceId?: number | null; athleteId?: number | null; payload: TenantSessionChargeNotReceivedPayload }
  >({
    mutationFn: ({ bookingId, chargeId, payload }) => markTenantSessionChargeNotReceived(bookingId, chargeId, payload),
    onSuccess: (_data, variables) => invalidateSessionPaymentQueries(queryClient, variables.occurrenceId, variables.athleteId),
  });
}
